import { useState, FormEvent } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { staffApi } from '@/api/staffApi';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface ForceChangePasswordPageProps {
  tempPassword: string;
  onSuccess: () => void;
}

export function ForceChangePasswordPage({ tempPassword, onSuccess }: ForceChangePasswordPageProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleChangePassword(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await staffApi.changePassword(tempPassword, newPassword);
      onSuccess();
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { message?: string; error?: string } } };
      setError(axErr?.response?.data?.message ?? axErr?.response?.data?.error ?? 'Failed to change password.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-surface-container p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-surface-container-lowest border border-outline-variant rounded-2xl p-8 shadow-lg">
          {/* Alert */}
          <div className="flex gap-3 mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-semibold mb-1">Set Your Password</p>
              <p>You're using a temporary password. Please set a new password to continue.</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">
                <Lock className="inline h-4 w-4 mr-1" />
                New Password
              </label>
              <Input
                type="password"
                placeholder="At least 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-on-surface mb-2">
                <Lock className="inline h-4 w-4 mr-1" />
                Confirm Password
              </label>
              <Input
                type="password"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              loading={loading}
              disabled={loading || !newPassword || !confirmPassword}
            >
              Set Password & Continue
            </Button>
          </form>

          {/* Info */}
          <p className="text-xs text-on-surface-variant text-center mt-4">
            Your password must be at least 8 characters long and contain a mix of uppercase, lowercase, numbers, and symbols for security.
          </p>
        </div>
      </div>
    </div>
  );
}
